import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import _ from 'lodash';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import Link from '@fuse/core/Link';
import Button from '@mui/material/Button';
import useJwtAuth from '../useJwtAuth';

/** HTTP statuses the login endpoint returns for a bad username/password. */
const CREDENTIAL_ERROR_STATUSES = [400, 401, 403];

/**
 * Form Validation Schema
 */
const schema = z.object({
	userName: z.string().nonempty('You must enter a username'),
	password: z
		.string()
		.min(4, 'Password is too short - must be at least 4 chars.')
		.nonempty('Please enter your password.'),
	remember: z.boolean().optional()
});

type FormType = z.infer<typeof schema>;

const defaultValues: FormType = {
	userName: '',
	password: '',
	remember: true
};

function JwtSignInForm() {
	const { signIn } = useJwtAuth();
	const [showPassword, setShowPassword] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const { control, formState, handleSubmit, setValue } = useForm<FormType>({
		mode: 'onChange',
		defaultValues,
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

	useEffect(() => {
		setValue('userName', '', { shouldDirty: true, shouldValidate: true });
		setValue('password', '', {
			shouldDirty: true,
			shouldValidate: true
		});
	}, [setValue]);

	function onSubmit(formData: FormType) {
		const { userName, password, remember } = formData;

		setFormError(null);

		signIn({
			userName,
			password,
			rememberMe: remember ?? false
		}).catch((error) => {
			const status = (error as { status?: number })?.status;

			// A 400/401/403 from the login endpoint means the credentials were
			// rejected; anything else (5xx, network failure, unexpected) is a
			// system error the user can't act on.
			setFormError(
				status && CREDENTIAL_ERROR_STATUSES.includes(status)
					? 'Incorrect username or password. Please try again.'
					: 'An error has occurred. Please contact the administrator.'
			);
		});
	}

	return (
		<form
			name="loginForm"
			noValidate
			className="flex w-full flex-col justify-center"
			onSubmit={handleSubmit(onSubmit)}
		>
			<Controller
				name="userName"
				control={control}
				render={({ field }) => (
					<TextField
						{...field}
						className="mb-6"
						label="Username"
						autoFocus
						type="text"
						error={!!errors.userName}
						helperText={errors?.userName?.message}
						variant="outlined"
						required
						fullWidth
					/>
				)}
			/>

			<Controller
				name="password"
				control={control}
				render={({ field }) => (
					<TextField
						{...field}
						className="mb-6"
						label="Password"
						type={showPassword ? 'text' : 'password'}
						error={!!errors.password}
						helperText={errors?.password?.message}
						variant="outlined"
						required
						fullWidth
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label={showPassword ? 'Hide password' : 'Show password'}
										onClick={() => setShowPassword((v) => !v)}
										edge="end"
										size="small"
									>
										{showPassword ? (
											<VisibilityOff fontSize="small" />
										) : (
											<Visibility fontSize="small" />
										)}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
				)}
			/>

			<div className="flex flex-col items-center justify-center sm:flex-row sm:justify-between">
				<Controller
					name="remember"
					control={control}
					render={({ field }) => (
						<FormControl>
							<FormControlLabel
								label="Remember me"
								control={
									<Checkbox
										size="small"
										{...field}
									/>
								}
							/>
						</FormControl>
					)}
				/>

				<Link
					className="text-md font-medium"
					to="/forgot-password"
				>
					Forgot password?
				</Link>
			</div>

			{formError && (
				<Alert
					severity="error"
					className="mt-4"
				>
					{formError}
				</Alert>
			)}

			<Button
				variant="contained"
				color="secondary"
				className="mt-4 w-full"
				aria-label="Sign in"
				disabled={_.isEmpty(dirtyFields) || !isValid}
				type="submit"
				size="large"
			>
				Sign in
			</Button>
		</form>
	);
}

export default JwtSignInForm;
